import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { Pencil, Percent, Trash2 } from 'lucide-react';

import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { DateRangePresetFilter } from '@/ui/DateRangePresetFilter';
import { resolveDateRange, type DateRangeValue } from '@/ui/dateRangePreset';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { MoneyInput } from '@/ui/MoneyInput';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Switch } from '@/ui/base/switch';
import { Textarea } from '@/ui/base/textarea';

import { useCenarioDeTela } from '../_dev/cenarioDeTela';
import { EstadoSimulado } from '../_dev/EstadoSimulado';
import DADOS_SIMULADOS from './dados-simulados.json';
import {
  formatarPercentual,
  gerarIdLocal,
  type DadosTabelaDePrecos,
  type ParametroRetencao,
} from './tipos';
import { TextoVigencia } from './VigenciaBadge';

/**
 * Aba "Parâmetros de Retenções": versões, por vigência, das alíquotas
 * usadas na apuração — INSS (piso, teto, base, alíquota efetiva), SEST/SENAT,
 * cooperativa, base de IR e outras retenções.
 *
 * Dado simulado por decisão de produto (ver `../_dev/cenarioDeTela.ts`): a
 * API do módulo financeiro ainda não existe. O CRUD abaixo é só em memória.
 */

const DADOS = DADOS_SIMULADOS as unknown as DadosTabelaDePrecos;

const COLUNAS_ESQUELETO = 8;

function itemVazio(): ParametroRetencao {
  return {
    id: '',
    nome: '',
    vigenciaInicio: new Date().toISOString().slice(0, 10),
    vigenciaFim: null,
    inssPiso: '0',
    inssTeto: '0',
    inssBaseCalculo: '0',
    inssAliquotaEfetiva: 0.022,
    sestSenatAliquota: 0.005,
    cooperativaAliquota: 0.015,
    irBasePct: 1,
    irNovaBasePct: 0.6,
    outrosAliquota: 0.024,
    ativa: true,
    observacoes: '',
  };
}

export interface ParametrosRetencoesTabHandle {
  abrirCriacao: () => void;
}

export const ParametrosRetencoesTab = forwardRef<ParametrosRetencoesTabHandle>((_props, ref) => {
  const cenario = useCenarioDeTela();
  const [itens, setItens] = useState<ParametroRetencao[]>([]);

  useEffect(() => {
    setItens(cenario === 'sucesso' ? DADOS.retencoes : []);
  }, [cenario]);

  const [filtro, setFiltro] = useState<Record<string, string>>({});
  const [vigencia, setVigencia] = useState<DateRangeValue>({ preset: 'ALL' });
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState<ParametroRetencao | null>(null);
  const [form, setForm] = useState<ParametroRetencao>(itemVazio());

  const abrirCriacao = () => {
    setEditando(null);
    setForm(itemVazio());
    setModalAberto(true);
  };

  const abrirEdicao = (item: ParametroRetencao) => {
    setEditando(item);
    setForm(item);
    setModalAberto(true);
  };

  useImperativeHandle(ref, () => ({ abrirCriacao }));

  const salvar = () => {
    if (editando) {
      setItens((atual) =>
        atual.map((i) => (i.id === editando.id ? { ...form, id: editando.id } : i)),
      );
    } else {
      setItens((atual) => [...atual, { ...form, id: gerarIdLocal() }]);
    }
    setModalAberto(false);
  };

  const excluir = (id: string) => {
    setItens((atual) => atual.filter((i) => i.id !== id));
  };

  const filtrados = useMemo(() => {
    const busca = (filtro.busca ?? '').trim().toLowerCase();
    const { from, to } = resolveDateRange(vigencia);
    return itens.filter((item) => {
      if (busca && !item.nome.toLowerCase().includes(busca)) return false;
      if (filtro.status === 'ATIVA' && !item.ativa) return false;
      if (filtro.status === 'INATIVA' && item.ativa) return false;
      if (from && item.vigenciaInicio < from) return false;
      if (to && item.vigenciaInicio > to) return false;
      return true;
    });
  }, [itens, filtro, vigencia]);

  const temFiltro = !!filtro.busca || !!filtro.status || vigencia.preset !== 'ALL';

  const camposFiltro: FilterFieldConfig[] = [
    { key: 'busca', label: 'Buscar', type: 'search', placeholder: 'Buscar por nome', flex: true },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'Todos',
      width: 'w-full sm:w-[140px]',
      options: [
        { value: 'ATIVA', label: 'Ativa' },
        { value: 'INATIVA', label: 'Inativa' },
      ],
    },
  ];

  const colunas: DataTableColumn<ParametroRetencao>[] = [
    { header: 'Nome', accessorKey: 'nome', cellClassName: 'font-medium' },
    {
      header: 'Vigência',
      cell: (r) => <TextoVigencia vigenciaInicio={r.vigenciaInicio} vigenciaFim={r.vigenciaFim} />,
    },
    {
      header: 'INSS piso',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (r) => <MoneyDisplay value={r.inssPiso} />,
    },
    {
      header: 'INSS teto',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (r) => <MoneyDisplay value={r.inssTeto} />,
    },
    {
      header: 'INSS alíq.',
      headerClassName: 'text-right',
      cellClassName: 'text-right font-mono text-sm',
      cell: (r) => formatarPercentual(r.inssAliquotaEfetiva),
    },
    {
      header: 'SEST/SENAT',
      headerClassName: 'text-right',
      cellClassName: 'text-right font-mono text-sm',
      cell: (r) => formatarPercentual(r.sestSenatAliquota),
    },
    {
      header: 'Coop.',
      headerClassName: 'text-right',
      cellClassName: 'text-right font-mono text-sm',
      cell: (r) => formatarPercentual(r.cooperativaAliquota),
    },
    {
      header: 'Nova base IR',
      headerClassName: 'text-right',
      cellClassName: 'text-right font-mono text-sm',
      cell: (r) => formatarPercentual(r.irNovaBasePct),
    },
    {
      header: 'Ativa',
      cell: (r) => (
        <Badge variant={r.ativa ? 'default' : 'secondary'}>{r.ativa ? 'Sim' : 'Não'}</Badge>
      ),
    },
    {
      // Regra 10 de interface do CLAUDE.md: coluna de ações sempre por último —
      // o protótipo (`ParametrosRetencoesTab.tsx`) tinha "Ativa" depois de "Ações".
      header: 'Ações',
      headerClassName: 'w-[96px]',
      cell: (r) => (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => abrirEdicao(r)}
            aria-label={`Editar ${r.nome}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <ConfirmDialog
            trigger={
              <Button variant="ghost" size="icon" aria-label={`Excluir ${r.nome}`}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            }
            title="Excluir versão"
            description="Esta versão de parâmetros será removida da lista."
            confirmLabel="Excluir"
            variant="destructive"
            onConfirm={() => excluir(r.id)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <FilterBar
        fields={camposFiltro}
        values={filtro}
        onChange={(chave, valor) => setFiltro((atual) => ({ ...atual, [chave]: valor }))}
        onClear={() => {
          setFiltro({});
          setVigencia({ preset: 'ALL' });
        }}
        hasActiveFilters={temFiltro}
      >
        <DateRangePresetFilter label="Vigência início" value={vigencia} onChange={setVigencia} />
      </FilterBar>

      <EstadoSimulado
        cenario={cenario}
        descricao="os parâmetros de retenções"
        colunasDoEsqueleto={COLUNAS_ESQUELETO}
      >
        <DataTable
          columns={colunas}
          data={filtrados}
          emptyIcon={Percent}
          emptyTitle={temFiltro ? 'Nenhum resultado' : 'Nenhum parâmetro cadastrado'}
          emptyDescription={
            temFiltro
              ? 'Ajuste os filtros para ver outros resultados.'
              : 'Cadastre a primeira versão dos parâmetros de retenções.'
          }
          emptyActionLabel={temFiltro ? undefined : 'Nova versão'}
          onEmptyAction={temFiltro ? undefined : abrirCriacao}
          ariaLabel="Versões de parâmetros de retenções"
        />
      </EstadoSimulado>

      <ResponsiveModal
        open={modalAberto}
        onOpenChange={setModalAberto}
        title={editando ? 'Editar versão de parâmetros' : 'Nova versão de parâmetros'}
        desktopMaxWidth="max-w-2xl"
        footer={
          <>
            <Button variant="outline" onClick={() => setModalAberto(false)}>
              Cancelar
            </Button>
            <Button onClick={salvar}>Salvar</Button>
          </>
        }
      >
        <div className="grid gap-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="ret-nome">Nome</Label>
              <Input
                id="ret-nome"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Ex.: Retenções 2026"
              />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch
                checked={form.ativa}
                onCheckedChange={(v) => setForm({ ...form, ativa: v })}
                id="ret-ativa"
              />
              <Label htmlFor="ret-ativa">Ativa</Label>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="ret-inicio">Vigência início</Label>
              <Input
                id="ret-inicio"
                type="date"
                value={form.vigenciaInicio}
                onChange={(e) => setForm({ ...form, vigenciaInicio: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ret-fim">Vigência fim</Label>
              <Input
                id="ret-fim"
                type="date"
                value={form.vigenciaFim ?? ''}
                onChange={(e) => setForm({ ...form, vigenciaFim: e.target.value || null })}
              />
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <h3 className="mb-3 text-sm font-semibold">INSS</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="ret-piso">Piso</Label>
                <MoneyInput
                  id="ret-piso"
                  value={form.inssPiso}
                  onChange={(v) => setForm({ ...form, inssPiso: v })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ret-teto">Teto</Label>
                <MoneyInput
                  id="ret-teto"
                  value={form.inssTeto}
                  onChange={(v) => setForm({ ...form, inssTeto: v })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ret-base">Base de cálculo</Label>
                <MoneyInput
                  id="ret-base"
                  value={form.inssBaseCalculo}
                  onChange={(v) => setForm({ ...form, inssBaseCalculo: v })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ret-aliq">Alíquota efetiva</Label>
                <Input
                  id="ret-aliq"
                  type="number"
                  step="0.0001"
                  min="0"
                  value={form.inssAliquotaEfetiva}
                  onChange={(e) =>
                    setForm({ ...form, inssAliquotaEfetiva: Number(e.target.value) || 0 })
                  }
                />
                <span className="text-xs text-muted-foreground">
                  {formatarPercentual(form.inssAliquotaEfetiva)}
                </span>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <h3 className="mb-3 text-sm font-semibold">Outras retenções</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="ret-sest">SEST/SENAT</Label>
                <Input
                  id="ret-sest"
                  type="number"
                  step="0.0001"
                  min="0"
                  value={form.sestSenatAliquota}
                  onChange={(e) =>
                    setForm({ ...form, sestSenatAliquota: Number(e.target.value) || 0 })
                  }
                />
                <span className="text-xs text-muted-foreground">
                  {formatarPercentual(form.sestSenatAliquota)}
                </span>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ret-coop">Cooperativa</Label>
                <Input
                  id="ret-coop"
                  type="number"
                  step="0.0001"
                  min="0"
                  value={form.cooperativaAliquota}
                  onChange={(e) =>
                    setForm({ ...form, cooperativaAliquota: Number(e.target.value) || 0 })
                  }
                />
                <span className="text-xs text-muted-foreground">
                  {formatarPercentual(form.cooperativaAliquota)}
                </span>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ret-irbase">IR base (% s/ bruto)</Label>
                <Input
                  id="ret-irbase"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.irBasePct}
                  onChange={(e) => setForm({ ...form, irBasePct: Number(e.target.value) || 0 })}
                />
                <span className="text-xs text-muted-foreground">
                  {formatarPercentual(form.irBasePct)}
                </span>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ret-irnova">Nova base IR (% reduzida)</Label>
                <Input
                  id="ret-irnova"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.irNovaBasePct}
                  onChange={(e) => setForm({ ...form, irNovaBasePct: Number(e.target.value) || 0 })}
                />
                <span className="text-xs text-muted-foreground">
                  {formatarPercentual(form.irNovaBasePct)}
                </span>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ret-outros">Outros</Label>
                <Input
                  id="ret-outros"
                  type="number"
                  step="0.0001"
                  min="0"
                  value={form.outrosAliquota}
                  onChange={(e) =>
                    setForm({ ...form, outrosAliquota: Number(e.target.value) || 0 })
                  }
                />
                <span className="text-xs text-muted-foreground">
                  {formatarPercentual(form.outrosAliquota)}
                </span>
              </div>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="ret-obs">Observações</Label>
            <Textarea
              id="ret-obs"
              rows={2}
              value={form.observacoes}
              onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
            />
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
});

ParametrosRetencoesTab.displayName = 'ParametrosRetencoesTab';
